import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Copy, Check, XCircle, CheckCircle2 } from "lucide-react";
import { useMutation, useLazyQuery } from "@apollo/client/react";
import { CREATE_PERSONAL_ROOM } from "@/graphql/mutations";
import { GET_PERSONAL_ROOMS, CHECK_ROOM_NAME_AVAILABILITY } from "@/graphql/queries";
import { useToast } from "@/hooks/use-toast";
import { trackDashboardEvent } from "@/lib/analytics";

interface CreatePersonalRoomDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  credits: number;
  onSuccess?: () => void;
}

// Helper to generate random room name: abc-def-ghi
const generateRoomName = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyz';
  const part = () => {
    let res = '';
    for (let i = 0; i < 3; i++) res += chars.charAt(Math.floor(Math.random() * chars.length));
    return res;
  };
  return `${part()}-${part()}-${part()}`;
};

const CreatePersonalRoomDialog = ({ open, onOpenChange, credits, onSuccess }: CreatePersonalRoomDialogProps) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [roomName, setRoomName] = useState("");
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [createdRoom, setCreatedRoom] = useState<{ title?: string; description?: string | null; roomName?: string; joinUrl?: string } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const { toast } = useToast();

  const [checkAvailabilityQuery] = useLazyQuery(CHECK_ROOM_NAME_AVAILABILITY, {
    fetchPolicy: "network-only",
  });

  const checkNameAvailability = useCallback((name: string) => {
    checkAvailabilityQuery({
      variables: { roomName: name },
    })
      .then((result) => {
        if (result.data) {
          setIsAvailable((result.data as { checkRoomNameAvailability: boolean }).checkRoomNameAvailability);
          setIsChecking(false);
        } else if (result.error) {
          setIsAvailable(false);
          setIsChecking(false);
        }
      })
      .catch(() => {
        setIsAvailable(false);
        setIsChecking(false);
      });
  }, [checkAvailabilityQuery]);

  const [createRoom, { loading }] = useMutation(CREATE_PERSONAL_ROOM, {
    onCompleted: (data: { createPersonalRoom?: { title?: string; description?: string | null; roomName?: string; joinUrl?: string } }) => {
      setCreatedRoom(data.createPersonalRoom ?? null);
      trackDashboardEvent("room_action", {
        action: "create_success",
        room_name: data.createPersonalRoom?.roomName,
      });
      toast({
        title: "Room Created!",
        description: "Your personal room is ready to use.",
      });
      if (onSuccess) onSuccess();
    },
    onError: (error) => {
      trackDashboardEvent("room_action", {
        action: "create_failure",
        room_name: roomName.trim(),
        error_message: error.message || "create_room_failed",
      });
      toast({
        title: "Error",
        description: error.message || "Failed to create room. Please try again.",
        variant: "destructive",
      });
    },
    refetchQueries: [{ query: GET_PERSONAL_ROOMS }],
  });

  // Initialize with random name when opened
  useEffect(() => {
    if (open && !createdRoom) {
      const initialName = generateRoomName();
      setRoomName(initialName);
      // Check initial availability
      setIsChecking(true);
      checkNameAvailability(initialName);
    }
  }, [open, createdRoom, checkNameAvailability]);

  // Debounced check when roomName changes
  useEffect(() => {
    if (!open || !roomName) return;

    setIsChecking(true);
    setIsAvailable(null);

    const timer = setTimeout(() => {
      if (roomName.length >= 3) {
        checkNameAvailability(roomName);
      } else {
        setIsChecking(false);
        setIsAvailable(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [roomName, open, checkNameAvailability]);

  const handleCreate = async () => {
    if (!title.trim()) {
      toast({
        title: "Error",
        description: "Room title is required.",
        variant: "destructive",
      });
      return;
    }

    if (!isAvailable) {
      toast({
        title: "Error",
        description: "Please select an available room URL.",
        variant: "destructive",
      });
      return;
    }

    try {
      trackDashboardEvent("room_action", {
        action: "create_attempt",
        room_name: roomName.trim(),
        has_description: Boolean(description),
      });
      await createRoom({
        variables: {
          input: {
            title: title.trim(),
            ...(description && { description }),
            roomName: roomName.trim(),
          },
        },
      });
    } catch (error) {
      // Error handled by onError callback
    }
  };

  const copyRoomLink = () => {
    if (!createdRoom) return;

    const baseUrl = typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
      ? `http://localhost:3001/${createdRoom.roomName}`
      : `https://meet.flowlabs.live/${createdRoom.roomName}`;

    navigator.clipboard.writeText(baseUrl);
    trackDashboardEvent("room_action", {
      action: "copy_link",
      room_name: createdRoom.roomName,
    });
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleClose = () => {
    setTitle("");
    setDescription("");
    setRoomName("");
    setIsAvailable(null);
    setCreatedRoom(null);
    setLinkCopied(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg bg-card border-border">
        {!createdRoom ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl">Create Personal Room</DialogTitle>
              <DialogDescription>
                Get your own permanent meeting room with a custom URL.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Room Details */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Room Title <span className="text-destructive">*</span></Label>
                  <Input
                    id="title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="bg-background border-border"
                    placeholder="e.g., Client Meetings, Design Reviews"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Visible only to you. You can edit this later.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="roomName">Custom URL <span className="text-destructive">*</span></Label>
                  <div className="relative">
                    <div className="flex items-center">
                      <span className="bg-muted px-3 py-2 border border-r-0 border-border rounded-l-md text-muted-foreground text-sm whitespace-nowrap">
                        meet.flowlabs.live/
                      </span>
                      <Input
                        id="roomName"
                        type="text"
                        value={roomName}
                        onChange={(e) => {
                          // Only allow alphanumeric and hyphens, lowercase
                          const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                          setRoomName(val);
                        }}
                        className={`bg-background border-border rounded-l-none pr-10 ${isAvailable === true ? "border-green-500 focus-visible:ring-green-500" :
                          isAvailable === false ? "border-red-500 focus-visible:ring-red-500" : ""
                          }`}
                        placeholder="my-custom-room"
                      />
                    </div>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {isChecking ? (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      ) : isAvailable === true ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : isAvailable === false ? (
                        <XCircle className="h-4 w-4 text-red-500" />
                      ) : null}
                    </div>
                  </div>
                  <div className="flex justify-between text-xs">
                    <p className="text-muted-foreground">
                      Only letters, numbers, and hyphens allowed.
                    </p>
                    {isAvailable === false && !isChecking && (
                      <p className="text-red-500 font-medium">
                        This URL is already taken.
                      </p>
                    )}
                    {isAvailable === true && !isChecking && (
                      <p className="text-green-500 font-medium">
                        Available!
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="bg-background border-border min-h-[80px]"
                    placeholder="Brief description of this room's purpose"
                  />
                  <p className="text-xs text-muted-foreground">
                    Visible only to you.
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={loading || !title.trim() || !isAvailable || isChecking}
                className="bg-gradient-hero text-white hover:opacity-90 font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Room"
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl">Room Created!</DialogTitle>
              <DialogDescription>
                Your personal room is ready. Share the link to invite others.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                <p className="text-sm font-medium mb-2">{createdRoom.title}</p>
                {createdRoom.description && (
                  <p className="text-sm text-muted-foreground mb-3">{createdRoom.description}</p>
                )}
                <div className="flex items-center gap-2 text-sm font-mono bg-background/50 p-2 rounded border border-border/50">
                  <span className="flex-1 truncate">meet.flowlabs.live/{createdRoom.roomName}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copyRoomLink}
                    className="h-8 px-2"
                  >
                    {linkCopied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                onClick={handleClose}
                className="w-full"
              >
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreatePersonalRoomDialog;
